import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import {
  Briefcase,
  FileText,
  Users,
  Upload,
  Calendar,
  MapPin,
  Clock,
  Star,
  Building2,
  CheckCircle2,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export const UserPortal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(() => {
    const valid = ['karriere', 'stellenmarkt', 'matching'];
    return valid.includes(tabFromUrl) ? tabFromUrl : 'karriere';
  });
  
  // Redirect non-admin users to home page
  const hasAdminAccess = user?.is_admin || user?.is_manager || user?.is_ticket_controller;
  if (!hasAdminAccess) {
    navigate('/', { replace: true });
    return null;
  }
  
  const userName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : user?.name || (t('home.user') || 'User');

  const tabs = [
    {
      id: 'karriere',
      labelKey: 'portal.karriere',
      icon: Briefcase,
      activeBorderClass: 'border-ngbc-primary',
      activeBgClass: 'bg-gradient-to-b from-ngbc-secondary/10 to-transparent',
      activeTextClass: 'text-ngbc-accent',
    },
    {
      id: 'stellenmarkt',
      labelKey: 'portal.stellenmarkt',
      icon: Building2,
      activeBorderClass: 'border-ngbc-primary',
      activeBgClass: 'bg-gradient-to-b from-ngbc-secondary/10 to-transparent',
      activeTextClass: 'text-ngbc-accent',
    },
    {
      id: 'matching',
      labelKey: 'portal.matching',
      icon: Sparkles,
      activeBorderClass: 'border-ngbc-accent',
      activeBgClass: 'bg-gradient-to-b from-ngbc-primary/10 to-transparent',
      activeTextClass: 'text-ngbc-accent',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Development View Banner - Only visible to admins */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-yellow-500 text-black text-center text-xs sm:text-sm font-bold py-1.5 sm:py-2 px-4 shadow-lg">
        DEVELOPMENT VIEW - Admin only
      </div>
      
      <Header />
      
      <div className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-20 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-ngbc-dark mb-2">
              Willkommen, {userName}!
            </h1>
            <p className="text-lg text-ngbc-dark-text">
              Ihr persönlicher Bereich für den Next Gen Business Cup
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-ngbc-beige rounded-2xl shadow-lg mb-8 overflow-hidden">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 min-w-[150px] px-6 py-4 flex items-center justify-center space-x-2 font-semibold transition-all border-b-4 ${
                      activeTab === tab.id
                        ? `${tab.activeBorderClass} ${tab.activeBgClass} ${tab.activeTextClass}`
                        : 'border-transparent text-ngbc-dark-text hover:bg-ngbc-secondary/30 hover:text-ngbc-primary'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{t(tab.labelKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-ngbc-beige rounded-2xl shadow-lg p-8">
            {activeTab === 'karriere' && <KarriereTab user={user} t={t} />}
            {activeTab === 'stellenmarkt' && <StellenmarktTab user={user} t={t} />}
            {activeTab === 'matching' && <MatchingTab user={user} t={t} />}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Karriere Tab - Lebenslauf Upload für AI CV Matching
const KarriereTab = ({ user, t }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';
  const fileInputRef = useRef(null);
  const [cvUploaded, setCvUploaded] = useState(false);
  const [cvFileName, setCvFileName] = useState('');
  const [desiredPosition, setDesiredPosition] = useState('');
  const [industry, setIndustry] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [aiReview, setAiReview] = useState(null);

  const fetchUserProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/user/profile`);
      const profile = res.data.user || {};
      const cv = res.data.cv;
      const claude = res.data.claude_review;

      setDesiredPosition(profile.desired_position || '');
      setIndustry(profile.industry || '');
      setExperienceLevel(profile.experience_level || '');
      setCvUploaded(Boolean(cv));
      setCvFileName(cv?.filename || '');
      setAiReview(claude?.result || (cv?.claude_review?.result ?? null));
    } catch (error) {
      console.error('Fehler beim Laden des Nutzerprofils:', error);
      setErrorMessage(error?.response?.data?.detail || 'Profil konnte nicht geladen werden');
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMessage('');
    setMessage('');
    setIsLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload-cv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setCvUploaded(true);
      setCvFileName(file.name);
      setMessage(response.data?.message || 'CV erfolgreich hochgeladen');
      await fetchUserProfile();
    } catch (error) {
      console.error('Upload-Fehler:', error);
      setErrorMessage(error?.response?.data?.detail || 'CV-Upload fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    setErrorMessage('');
    setMessage('');
    setIsLoading(true);

    try {
      const response = await axios.put(`${API_URL}/user/profile`, {
        desired_position: desiredPosition,
        industry,
        experience_level: experienceLevel,
      });
      setMessage(response.data?.message || 'Profil erfolgreich gespeichert');
      await fetchUserProfile();
    } catch (error) {
      console.error('Profil speichern fehlgeschlagen:', error);
      setErrorMessage(error?.response?.data?.detail || 'Profilspeicherung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const runAiAnalysis = async () => {
    setErrorMessage('');
    setMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/user/cv/screen`);
      setAiReview(response.data?.data?.review?.result || response.data?.data?.review || response.data?.data || null);
      setMessage(response.data?.message || 'AI-Screening abgeschlossen');
      await fetchUserProfile();
    } catch (error) {
      console.error('AI-Analyse fehlgeschlagen:', error);
      setErrorMessage(error?.response?.data?.detail || 'AI-Screening fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-ngbc-dark mb-2">{t('portal.karrierePortal')}</h2>
        <p className="text-ngbc-dark-text">{t('portal.karriereDesc')}</p>
      </div>

      {message && <div className="rounded-lg bg-green-100 px-4 py-3 text-green-800">{message}</div>}
      {errorMessage && <div className="rounded-lg bg-red-100 px-4 py-3 text-red-800">{errorMessage}</div>}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={onFileChange}
      />

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-ngbc-secondary/20 to-ngbc-primary/10 rounded-xl p-6 border-2 border-ngbc-primary/20">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-ngbc-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-ngbc-dark">{t('portal.ihrLebenslauf')}</h3>
          </div>

          {!cvUploaded ? (
            <div className="space-y-4">
              <div
                className="bg-ngbc-beige rounded-lg p-8 border-2 border-dashed border-ngbc-dark-text hover:border-ngbc-primary transition-all cursor-pointer"
                onClick={openFileSelector}
              >
                <div className="text-center">
                  <Upload className="w-12 h-12 text-ngbc-muted mx-auto mb-4" />
                  <p className="text-ngbc-dark font-semibold mb-2">{t('portal.cvUpload')}</p>
                  <p className="text-sm text-ngbc-dark-text mb-4">{t('portal.cvFormats')}</p>
                  <Button className="bg-ngbc-primary hover:bg-ngbc-accent">{t('portal.dateiAuswaehlen')}</Button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">{t('portal.aiMatchingTitle')}</p>
                    <p className="text-sm text-blue-800">{t('portal.aiMatchingDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-ngbc-beige rounded-lg p-6 border-2 border-green-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <span className="font-semibold text-ngbc-dark">{cvFileName}</span>
                  </div>
                  <button
                    className="text-sm text-ngbc-primary hover:text-ngbc-accent font-medium"
                    onClick={openFileSelector}
                  >
                    {t('portal.ersetzen')}
                  </button>
                </div>
                <p className="text-sm text-ngbc-dark-text">
                  {t('portal.hochgeladenAm')} {new Date().toLocaleDateString('de-DE')}
                </p>
              </div>

              <Button
                onClick={runAiAnalysis}
                className="w-full bg-ngbc-accent hover:bg-ngbc-accent/90"
                disabled={isLoading}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('portal.aiAnalyseStarten')}
              </Button>

              {aiReview && (
                <div className="bg-ngbc-beige rounded-lg p-4 border border-ngbc-dark-text">
                  <h4 className="font-semibold mb-2">AI Screening Ergebnis</h4>
                  <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(aiReview, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-ngbc-beige rounded-xl p-6 shadow-sm border border-ngbc-secondary">
            <h3 className="text-xl font-bold text-ngbc-dark mb-4">{t('portal.ihrProfil')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">{t('portal.gewuenschtePosition')}</label>
                <input
                  type="text"
                  value={desiredPosition}
                  onChange={(e) => setDesiredPosition(e.target.value)}
                  placeholder={t('portal.positionPlaceholder')}
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">{t('portal.branche')}</label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                >
                  <option value="">{t('portal.branche')}</option>
                  <option value="IT & Software">IT & Software</option>
                  <option value="Marketing & Kommunikation">Marketing & Kommunikation</option>
                  <option value="Finanzen & Banking">Finanzen & Banking</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Andere">Andere</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">{t('portal.erfahrungslevel')}</label>
                <select
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                >
                  <option value="">{t('portal.erfahrungslevel')}</option>
                  <option value="Berufseinsteiger">Berufseinsteiger</option>
                  <option value="Junior (1-3 Jahre)">Junior (1-3 Jahre)</option>
                  <option value="Mid-Level (3-5 Jahre)">Mid-Level (3-5 Jahre)</option>
                  <option value="Senior (5+ Jahre)">Senior (5+ Jahre)</option>
                </select>
              </div>
              <Button onClick={saveProfile} className="w-full bg-ngbc-primary hover:bg-ngbc-accent" disabled={isLoading}>
                {t('portal.profilSpeichern')}
              </Button>
            </div>
          </div>

          <div className="bg-gradient-to-br from-ngbc-accent to-ngbc-primary rounded-xl p-6 text-white">
            <h4 className="text-lg font-bold mb-2">{t('portal.tip')}</h4>
            <p className="text-sm text-white/90">{t('portal.tipText')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stellenmarkt Tab - Job-Anforderungen & Stellenausschreibungen
const StellenmarktTab = ({ user, t }) => {
  const isCompany = user?.user_type === 'company';

  const jobListings = [
    {
      id: 1,
      company: 'TechCorp GmbH',
      position: 'Senior Software Developer',
      location: 'München',
      type: 'Vollzeit',
      salary: '70.000 - 95.000 €',
      tags: ['React', 'Node.js', 'AWS']
    },
    {
      id: 2,
      company: 'Marketing Pro AG',
      position: 'Marketing Manager',
      location: 'Berlin',
      type: 'Vollzeit',
      salary: '55.000 - 70.000 €',
      tags: ['Digital Marketing', 'SEO', 'Social Media']
    },
    {
      id: 3,
      company: 'FinanceFirst',
      position: 'Junior Consultant',
      location: 'Frankfurt',
      type: 'Vollzeit',
      salary: '45.000 - 55.000 €',
      tags: ['Consulting', 'Finance', 'Analytics']
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-ngbc-dark mb-2">{t('portal.stellenmarktTitle')}</h2>
          <p className="text-ngbc-dark-text">
            {isCompany 
              ? t('portal.stellenmarktCompany') 
              : t('portal.stellenmarktUser')}
          </p>
        </div>
        {isCompany && (
          <Button className="bg-ngbc-accent hover:bg-ngbc-accent/90">
            <Building2 className="w-4 h-4 mr-2" />
            {t('portal.neueStelleAusschreiben')}
          </Button>
        )}
      </div>

      {isCompany ? (
        <div className="bg-ngbc-beige rounded-xl p-6 border-2 border-ngbc-secondary">
          <h3 className="text-xl font-bold text-ngbc-dark mb-6">{t('portal.neueStelleErstellen')}</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">
                  {t('portal.positionsbezeichnung')}
                </label>
                <input
                  type="text"
                  placeholder="z.B. Senior Software Developer"
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">
                  {t('portal.standort')}
                </label>
                <input
                  type="text"
                  placeholder="z.B. München"
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">
                  {t('portal.beschaeftigungsart')}
                </label>
                <select className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent">
                  <option>{t('portal.vollzeit')}</option>
                  <option>{t('portal.teilzeit')}</option>
                  <option>{t('portal.werkstudent')}</option>
                  <option>{t('portal.praktikum')}</option>
                </select>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">
                  {t('portal.gehaltsspanne')}
                </label>
                <input
                  type="text"
                  placeholder="z.B. 70.000 - 90.000 €"
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">
                  {t('portal.skillsLabel')}
                </label>
                <input
                  type="text"
                  placeholder={t('portal.skillsPlaceholder')}
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ngbc-dark mb-2">
                  {t('portal.stellenbeschreibung')}
                </label>
                <textarea
                  rows="4"
                  placeholder={t('portal.beschreibungPlaceholder')}
                  className="w-full px-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-4">
            <Button variant="outline" className="border-ngbc-dark-text">
              {t('portal.abbrechen')}
            </Button>
            <Button className="bg-ngbc-primary hover:bg-ngbc-accent">
              {t('portal.stelleVeröffentlichen')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {jobListings.map((job) => (
            <div
              key={job.id}
              className="bg-ngbc-beige rounded-xl p-6 shadow-sm border-2 border-ngbc-secondary hover:border-ngbc-primary transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-ngbc-secondary to-ngbc-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-ngbc-dark mb-1">{job.position}</h3>
                    <p className="text-ngbc-dark-text font-medium">{job.company}</p>
                  </div>
                </div>
                <Button className="bg-ngbc-primary hover:bg-ngbc-accent">
                  {t('portal.bewerben')}
                </Button>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-ngbc-dark-text">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {job.location}
                </div>
                <div className="flex items-center">
                  <Briefcase className="w-4 h-4 mr-1" />
                  {job.type}
                </div>
                <div className="flex items-center font-semibold text-ngbc-accent">
                  💰 {job.salary}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {job.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-ngbc-secondary/30 text-ngbc-accent text-sm font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Matching Tab - AI-basiertes Matching + Terminvergabe
const MatchingTab = ({ user, t }) => {
  const isCompany = user?.user_type === 'company';

  const matches = [
    {
      id: 1,
      name: isCompany ? 'Max Mustermann' : 'TechCorp GmbH',
      position: isCompany ? 'Software Developer' : 'Senior Developer Position',
      matchScore: 95,
      skills: ['React', 'Node.js', 'AWS'],
      available: true
    },
    {
      id: 2,
      name: isCompany ? 'Anna Schmidt' : 'Marketing Pro AG',
      position: isCompany ? 'Marketing Manager' : 'Marketing Lead Position',
      matchScore: 88,
      skills: ['Digital Marketing', 'SEO', 'Analytics'],
      available: true
    },
    {
      id: 3,
      name: isCompany ? 'Tom Weber' : 'FinanceFirst',
      position: isCompany ? 'Junior Consultant' : 'Consulting Position',
      matchScore: 82,
      skills: ['Consulting', 'Finance', 'Excel'],
      available: false
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-ngbc-dark mb-2 flex items-center">
          <Sparkles className="w-8 h-8 text-ngbc-accent mr-3" />
          {t('portal.matchingTitle')}
        </h2>
        <p className="text-ngbc-dark-text">
          {isCompany 
            ? t('portal.matchingCompany') 
            : t('portal.matchingUser')}
        </p>
      </div>

      <div className="bg-gradient-to-r from-ngbc-primary to-ngbc-accent rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-2">
              {t('portal.topMatchesGefunden', { n: matches.filter(m => m.matchScore >= 85).length })}
            </h3>
            <p className="text-white/90">
              {t('portal.basierendAufProfil')}
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{matches[0].matchScore}%</div>
            <div className="text-sm text-white/80">{t('portal.besterMatch')}</div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {matches.map((match) => (
          <div
            key={match.id}
            className="bg-ngbc-beige rounded-xl p-6 shadow-sm border-2 border-ngbc-secondary hover:border-ngbc-primary transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-16 h-16 bg-gradient-to-br from-ngbc-secondary to-ngbc-primary rounded-full flex items-center justify-center flex-shrink-0">
                  {isCompany ? (
                    <Users className="w-8 h-8 text-white" />
                  ) : (
                    <Building2 className="w-8 h-8 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-ngbc-dark">{match.name}</h3>
                    <div className="flex items-center space-x-1">
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                        match.matchScore >= 90 
                          ? 'bg-green-100 text-green-700' 
                          : match.matchScore >= 80 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-ngbc-secondary/50 text-ngbc-dark'
                      }`}>
                        {match.matchScore}% {t('portal.match')}
                      </div>
                    </div>
                  </div>
                  <p className="text-ngbc-dark-text mb-3">{match.position}</p>
                  <div className="flex flex-wrap gap-2">
                    {match.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-ngbc-secondary/30 text-ngbc-accent text-sm font-medium rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col space-y-2 ml-4">
                {match.available ? (
                  <>
                    <Button className="bg-ngbc-accent hover:bg-ngbc-accent/90 whitespace-nowrap">
                      <Calendar className="w-4 h-4 mr-2" />
                      {t('portal.terminVereinbaren')}
                    </Button>
                    <Button variant="outline" className="border-ngbc-primary text-ngbc-primary hover:bg-ngbc-secondary/10 whitespace-nowrap">
                      <ArrowRight className="w-4 h-4 mr-2" />
                      {t('portal.profilAnsehen')}
                    </Button>
                  </>
                ) : (
                  <div className="text-sm text-ngbc-muted text-center px-4 py-2 bg-ngbc-secondary/50 rounded-lg">
                    {t('portal.bereitsGebucht')}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gebuchte Termine */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-xl font-bold text-ngbc-dark mb-4 flex items-center">
          <Calendar className="w-6 h-6 text-ngbc-primary mr-2" />
          {t('portal.gebuchteTermine')}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-ngbc-beige rounded-lg p-4 border-l-4 border-ngbc-accent">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-ngbc-dark">
                {isCompany ? 'Max Mustermann' : 'TechCorp GmbH'}
              </span>
              <span className="text-sm text-ngbc-accent font-semibold">95% Match</span>
            </div>
            <div className="flex items-center text-sm text-ngbc-dark-text mb-1">
              <Clock className="w-4 h-4 mr-1" />
              07.02.2026, 14:00 - 14:30
            </div>
            <div className="flex items-center text-sm text-ngbc-dark-text">
              <MapPin className="w-4 h-4 mr-1" />
              Raum B, 2. OG
            </div>
          </div>
          
          <div className="bg-ngbc-beige rounded-lg p-4 border-l-4 border-ngbc-primary">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-ngbc-dark">
                {isCompany ? 'Anna Schmidt' : 'Marketing Pro AG'}
              </span>
              <span className="text-sm text-ngbc-primary font-semibold">88% Match</span>
            </div>
            <div className="flex items-center text-sm text-ngbc-dark-text mb-1">
              <Clock className="w-4 h-4 mr-1" />
              07.02.2026, 15:00 - 15:30
            </div>
            <div className="flex items-center text-sm text-ngbc-dark-text">
              <MapPin className="w-4 h-4 mr-1" />
              Raum C, 2. OG
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
